import React, { useState, useMemo, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import { useApp } from '../../context/AppContext';
import Card from '../../components/Card/Card';
import Timeline from '../../components/Timeline/Timeline';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function calcDuration(r) {
  if (!r.from) return 1;
  const s = new Date(r.from);
  const e = r.to ? new Date(r.to) : new Date('2025-03-30');
  return Math.max(1, Math.round(((e - s) / 1000 / 60 / 60 / 24 / 365) * 10) / 10);
}

export default function OperatorHistory() {
  const { D } = useApp();
  const histories = useMemo(() => D.histories || [], [D]);

  const MSNS = useMemo(() => {
    const seen = new Set();
    histories.forEach(r => { if (r.msn) seen.add(String(r.msn)); });
    return Array.from(seen).sort((a, b) => Number(a) - Number(b));
  }, [histories]);

  const [selectedMSN, setSelectedMSN] = useState('');
  const [searchQuery, setSearchQuery]  = useState('');

  useEffect(() => {
    setSelectedMSN(MSNS[0] || '');
    setSearchQuery('');
  }, [MSNS]);

  const { timelineRecs, detailFacts, barData, barOpts } = useMemo(() => {
    if (!selectedMSN) {
      return { timelineRecs: [], detailFacts: null, barData: null, barOpts: null };
    }

    const recs = histories.filter(r => String(r.msn) === String(selectedMSN));

    const timelineRecs = histories
      .filter(r => String(r.msn) === String(selectedMSN))
      .sort((a, b) => {
        const da = a.from ? new Date(a.from) : new Date(0);
        const db = b.from ? new Date(b.from) : new Date(0);
        return da - db;
      })
      .map(r => ({
        op:   r.operator || r.op || 'Unknown',
        from: r.from || null,
        to:   r.to   || null,
        reg:  r.reg  || r.registration || null,
        type: r.type || r.aircraftType || null,
      }));

    const first = recs[0] || {};
    const detailFacts = {
      msn:           selectedMSN,
      aircraftType:  first.type || first.aircraftType || '—',
      firstFlight:   first.firstFlight || first.first_flight || '—',
      engineSubtype: first.engineSubtype || first.engine || '—',
      numOperators:  new Set(recs.map(r => r.operator || r.op).filter(Boolean)).size,
    };

    const sorted = [...recs].sort((a, b) => {
      const da = a.from ? new Date(a.from) : new Date(0);
      const db = b.from ? new Date(b.from) : new Date(0);
      return da - db;
    });

    const labels    = sorted.map(r => r.operator || r.op || 'Unknown');
    const durations = sorted.map(r => calcDuration(r));

    const barData = {
      labels,
      datasets: [{
        label: 'Years operated',
        data: durations,
        backgroundColor: '#1246d6',
        borderRadius: 4,
        barThickness: 16,
      }],
    };

    const barOpts = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.parsed.x} yr${ctx.parsed.x !== 1 ? 's' : ''}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(0,0,0,.06)' },
          ticks: { font: { size: 10 } },
          title: { display: true, text: 'Years', font: { size: 10 } },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 10 } },
        },
      },
    };

    return { timelineRecs, detailFacts, barData, barOpts };
  }, [selectedMSN, histories]);

  const allRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return histories.filter(r => {
      if (!q) return true;
      return (
        String(r.msn || '').toLowerCase().includes(q) ||
        (r.type          || r.aircraftType || '').toLowerCase().includes(q) ||
        (r.operator      || r.op           || '').toLowerCase().includes(q) ||
        (r.reg           || r.registration || '').toLowerCase().includes(q) ||
        (r.engine        || r.engineSubtype|| '').toLowerCase().includes(q)
      );
    });
  }, [searchQuery, histories]);

  const handleRowClick = msn => {
    setSelectedMSN(String(msn));
  };

  const msnSelectEl = (
    <select
      className="fsel"
      value={selectedMSN}
      onChange={e => setSelectedMSN(e.target.value)}
    >
      {MSNS.length === 0 && <option value="">No data</option>}
      {MSNS.map(msn => (
        <option key={msn} value={msn}>MSN {msn}</option>
      ))}
    </select>
  );

  return (
    <div className="page-content">
      <div className="g2">
        <Card
          title="Operator Ownership Chain"
          icon="📜"
          iconClass="ip"
          noPad
          headerRight={msnSelectEl}
        >
          <div className="tline" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <Timeline records={timelineRecs} />
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card title="Aircraft Details" icon="✈" iconClass="ib">
            {!selectedMSN || !detailFacts ? (
              <div className="empty">Select an MSN to view aircraft details.</div>
            ) : (
              <table>
                <tbody>
                  <tr>
                    <td style={{ color: 'var(--tx3)', width: '40%' }}>MSN</td>
                    <td><strong>{detailFacts.msn}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--tx3)' }}>Aircraft Type</td>
                    <td>{detailFacts.aircraftType}</td>
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--tx3)' }}>First Flight</td>
                    <td>{detailFacts.firstFlight}</td>
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--tx3)' }}>Engine Subtype</td>
                    <td>{detailFacts.engineSubtype}</td>
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--tx3)' }}>No. of Operators</td>
                    <td><strong>{detailFacts.numOperators}</strong></td>
                  </tr>
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Operators Over Time" icon="📈" iconClass="it">
            {!selectedMSN || !barData ? (
              <div className="empty">Select an MSN to view operator timeline.</div>
            ) : (
              <div className="cwsm">
                <Bar data={barData} options={barOpts} />
              </div>
            )}
          </Card>
        </div>
      </div>

      <Card
        title="All Aircraft Histories"
        icon="🗂️"
        iconClass="ib"
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search histories…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>MSN</th>
                <th>Aircraft Type</th>
                <th>Operator</th>
                <th>Registration</th>
                <th>From</th>
                <th>To</th>
                <th>First Flight</th>
                <th>Engine</th>
              </tr>
            </thead>
            <tbody>
              {allRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No history records match the search.
                  </td>
                </tr>
              ) : (
                allRows.map((r, i) => {
                  const msn  = String(r.msn || '');
                  const isSelected = msn === String(selectedMSN);
                  return (
                    <tr
                      key={i}
                      style={{
                        cursor: 'pointer',
                        background: isSelected ? 'var(--acc-lt)' : undefined,
                      }}
                      onClick={() => msn && handleRowClick(msn)}
                    >
                      <td><strong>{msn || '—'}</strong></td>
                      <td>{r.type || r.aircraftType || '—'}</td>
                      <td>{r.operator || r.op || '—'}</td>
                      <td>{r.reg || r.registration || '—'}</td>
                      <td>{r.from || '—'}</td>
                      <td>{r.to || 'Present'}</td>
                      <td>{r.firstFlight || r.first_flight || '—'}</td>
                      <td>{r.engine || r.engineSubtype || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
